// useFreeTierToday — public-readable Daily Acca + Founder's Pick.
//
// RLS allows anon SELECT on free_tier_daily_acca / free_tier_founders_pick
// for rows in the last 7 days. This hook fetches today's row for each
// (single row each, by acca_date / pick_date = current_date), plus the
// most recent settled outcome from the prior 7 days for the
// "yesterday's pick won" teaser block.
//
// Doctrine: never fabricate. If no row exists for today (cron hasn't
// run yet, upstream desk_picks all abstained, etc) the hook returns
// nulls — the page must render the empty/awaiting-data variant.

import { useCallback, useEffect, useMemo, useState } from "react";
import { getBrowserClient } from "../supabase/browser.ts";
import { useUserTier } from "../auth/useUserTier.ts";
import { getEffectiveTier } from "./getEffectiveTier.ts";

export interface FreeAccaLeg {
  fixture_id: string;
  market: string;
  selection: string;
  odds: number;
  reasoning?: string | null;
  // Optional fields populated by joining onto canonical_fixtures during
  // generation — graceful fallbacks below if absent.
  home_team?: string;
  away_team?: string;
  competition?: string;
  kickoff_at?: string;
}

export interface FreeAcca {
  id: string;
  acca_date: string;
  legs: FreeAccaLeg[];
  combined_odds: number;
  leg_count: number;
  confidence: number | null;
  outcome: "won" | "lost" | "pending" | "void" | null;
  generated_at: string | null;
}

export interface FoundersPick {
  id: string;
  pick_date: string;
  fixture_id: string;
  market: string;
  selection: string;
  odds: number;
  source: string | null;
  desk_pick_id: string | null;
  reasoning: string | null;
  outcome: "won" | "lost" | "pending" | "void" | null;
  generated_at: string | null;
}

export interface FreeTierTodayState {
  acca: FreeAcca | null;
  founder: FoundersPick | null;
  /** Most recent acca with a non-pending outcome — for the "yesterday won" teaser. */
  recentAccaResult: FreeAcca | null;
  /** Most recent founder's pick with a non-pending outcome. */
  recentFounderResult: FoundersPick | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

function asN(v: unknown): number {
  if (typeof v === "number") return v;
  if (typeof v === "string") return Number(v);
  return Number.NaN;
}

function normLegs(raw: unknown): FreeAccaLeg[] {
  if (!Array.isArray(raw)) return [];
  return (raw as Array<Record<string, unknown>>)
    .map((leg): FreeAccaLeg | null => {
      const fixture_id = typeof leg.fixture_id === "string" ? leg.fixture_id : null;
      const market = typeof leg.market === "string" ? leg.market : null;
      const selection = typeof leg.selection === "string" ? leg.selection : null;
      if (!fixture_id || !market || !selection) return null;
      return {
        fixture_id,
        market,
        selection,
        odds: asN(leg.odds),
        reasoning: typeof leg.reasoning === "string" ? leg.reasoning : null,
        home_team: typeof leg.home_team === "string" ? leg.home_team : undefined,
        away_team: typeof leg.away_team === "string" ? leg.away_team : undefined,
        competition: typeof leg.competition === "string" ? leg.competition : undefined,
        kickoff_at: typeof leg.kickoff_at === "string" ? leg.kickoff_at : undefined,
      };
    })
    .filter((x): x is FreeAccaLeg => x !== null);
}

function normAcca(row: Record<string, unknown> | null): FreeAcca | null {
  if (!row || typeof row.id !== "string") return null;
  return {
    id: row.id,
    acca_date: typeof row.acca_date === "string" ? row.acca_date : "",
    legs: normLegs(row.legs),
    combined_odds: asN(row.combined_odds),
    leg_count: Math.round(asN(row.leg_count)),
    confidence: typeof row.confidence === "number" ? row.confidence : null,
    outcome:
      row.outcome === "won" || row.outcome === "lost" || row.outcome === "pending" || row.outcome === "void"
        ? row.outcome
        : null,
    generated_at: typeof row.generated_at === "string" ? row.generated_at : null,
  };
}

function normFounder(row: Record<string, unknown> | null): FoundersPick | null {
  if (!row || typeof row.id !== "string") return null;
  return {
    id: row.id,
    pick_date: typeof row.pick_date === "string" ? row.pick_date : "",
    fixture_id: typeof row.fixture_id === "string" ? row.fixture_id : "",
    market: typeof row.market === "string" ? row.market : "",
    selection: typeof row.selection === "string" ? row.selection : "",
    odds: asN(row.odds),
    source: typeof row.source === "string" ? row.source : null,
    desk_pick_id: typeof row.desk_pick_id === "string" ? row.desk_pick_id : null,
    reasoning: typeof row.reasoning === "string" ? row.reasoning : null,
    outcome:
      row.outcome === "won" || row.outcome === "lost" || row.outcome === "pending" || row.outcome === "void"
        ? row.outcome
        : null,
    generated_at: typeof row.generated_at === "string" ? row.generated_at : null,
  };
}

export function useFreeTierToday(): FreeTierTodayState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { tierCode } = useUserTier();
  const effectiveTier = getEffectiveTier(tierCode);
  const [acca, setAcca] = useState<FreeAcca | null>(null);
  const [founder, setFounder] = useState<FoundersPick | null>(null);
  const [recentAccaResult, setRecentAccaResult] = useState<FreeAcca | null>(null);
  const [recentFounderResult, setRecentFounderResult] = useState<FoundersPick | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);

    const today = new Date().toISOString().slice(0, 10);

    // db/types.ts may not include the free_tier_* tables yet; cast.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sb = supabase as any;

    // Phase I4 — fetch the tier variant matching the user's effective
    // tier. Anon/free → tier='free' (3-leg). Pro hierarchy → tier='pro'
    // (5-leg). Both rows live in free_tier_daily_acca keyed on
    // (acca_date, tier).
    const [accaRes, founderRes, recentAccaRes, recentFounderRes] = await Promise.all([
      sb
        .from("free_tier_daily_acca")
        .select("*")
        .eq("acca_date", today)
        .eq("tier", effectiveTier)
        .maybeSingle(),
      sb.from("free_tier_founders_pick").select("*").eq("pick_date", today).maybeSingle(),
      sb
        .from("free_tier_daily_acca")
        .select("*")
        .eq("tier", effectiveTier)
        .neq("outcome", "pending")
        .not("outcome", "is", null)
        .order("acca_date", { ascending: false })
        .limit(1),
      sb
        .from("free_tier_founders_pick")
        .select("*")
        .neq("outcome", "pending")
        .not("outcome", "is", null)
        .order("pick_date", { ascending: false })
        .limit(1),
    ]);

    const firstErr =
      accaRes.error?.message ??
      founderRes.error?.message ??
      recentAccaRes.error?.message ??
      recentFounderRes.error?.message ??
      null;
    if (firstErr) setError(firstErr);

    setAcca(normAcca(accaRes.data ?? null));
    setFounder(normFounder(founderRes.data ?? null));
    setRecentAccaResult(
      normAcca(Array.isArray(recentAccaRes.data) ? recentAccaRes.data[0] ?? null : null),
    );
    setRecentFounderResult(
      normFounder(Array.isArray(recentFounderRes.data) ? recentFounderRes.data[0] ?? null : null),
    );
    setLoading(false);
  }, [supabase, effectiveTier]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return useMemo<FreeTierTodayState>(
    () => ({ acca, founder, recentAccaResult, recentFounderResult, loading, error, refetch }),
    [acca, founder, recentAccaResult, recentFounderResult, loading, error, refetch],
  );
}
